import React, { useEffect, useState, useCallback, useRef } from 'react';
import { docService, sitesService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useDocumentTypes } from '../context/DocumentTypesContext';
import _ from 'lodash';
import { Link } from 'react-router-dom';
import { FaEye } from 'react-icons/fa';
import { MdDownload } from 'react-icons/md';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { HiPlus, HiX, HiTrash, HiSearch, HiCheckCircle, HiExclamationCircle } from 'react-icons/hi';
import HighlightedText from '../components/HighlightedText';
import { apiError } from '../utils/apiError';
import { DOC_STATUS } from '../theme/tokens';
import {
  HEADER_RULE,
  ROW_RULE,
  STICKY_CHECKBOX_W,
  STICKY_LEFT_EDGE,
  STICKY_RIGHT_EDGE,
  stickyBg,
  rowClass,
} from '../theme/table';

// Just the extension, on a neutral chip. The previous map assigned a different
// colour per file type and per document type, which had to be edited for every
// new type and spent colour — reserved here for status — on something the text
// already says.
const getFileExt = (filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  if (!ext || ext === filename?.toLowerCase()) return 'PDF';
  return ext.toUpperCase().slice(0, 4);
};

const PAGE_SIZE = 20;

// One neutral badge style for every document type. A per-type colour map had to
// be edited for each new type, and inventing a colour per catalog entry would
// cycle hues that stop being distinguishable. Colour stays reserved for status;
// the type is carried by its label.
const Pagination = ({ page, totalPages, onChange }) => {
  if (totalPages <= 1) return null;

  const getPages = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 4) return [1, 2, 3, 4, 5, '...', totalPages];
    if (page >= totalPages - 3) return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, '...', page - 1, page, page + 1, '...', totalPages];
  };

  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
        className="px-3 py-1.5 border border-slate-200 rounded text-sm hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        ‹
      </button>
      {getPages().map((p, i) =>
        p === '...' ? (
          <span key={`ellipsis-${i}`} className="px-2 py-1.5 text-slate-400 text-sm">…</span>
        ) : (
          <button
            key={p}
            onClick={() => onChange(p)}
            className={`px-3 py-1.5 border rounded text-sm ${
              p === page
                ? 'bg-blue-600 border-blue-600 text-white font-medium'
                : 'border-slate-200 hover:bg-slate-50 text-slate-700'
            }`}
          >
            {p}
          </button>
        )
      )}
      <button
        onClick={() => onChange(page + 1)}
        disabled={page === totalPages}
        className="px-3 py-1.5 border border-slate-200 rounded text-sm hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        ›
      </button>
    </div>
  );
};

const SortIcon = ({ field, sortBy, sortOrder }) => {
  if (sortBy !== field) return <span className="ml-1 text-slate-300">↕</span>;
  return <span className="ml-1 text-blue-600">{sortOrder === 'asc' ? '↑' : '↓'}</span>;
};

const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const formatDateTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const DocumentsPage = () => {
  const { user, allowedDocTypes, userSites } = useAuth();
  const { keys: allTypeKeys, labelOf } = useDocumentTypes();
  const [documents, setDocuments] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [siteMap, setSiteMap] = useState({});
  // Filters + sort — all in local state, NOT pushed to URL
  const [searchQuery, setSearchQuery] = useState('');
  const [qMode, setQMode] = useState('or');
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Build site_id → { name, colorClass } map from userSites + all sites for superusers
  useEffect(() => {
    const buildMap = (sites) => {
      const map = {};
      sites.forEach((s, i) => {
        map[s.site_id] = {
          name: s.site_name,
        };
      });
      setSiteMap(map);
    };

    if (user?.is_superuser) {
      sitesService.list().then((all) => {
        buildMap(all.map((s, i) => ({ site_id: s.id, site_name: s.name ?? s.slug, idx: i })));
      }).catch(() => buildMap(userSites));
    } else {
      buildMap(userSites);
    }
  }, [user, userSites]);

  // Multi-term search inputs (controlled)
  const [searchTerms, setSearchTerms] = useState(['']);

  // Bulk selection
  const [selected, setSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  // Delete confirm
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);
  const [showLegend, setShowLegend] = useState(false);
  const toastTimerRef = useRef(null);

  const showToast = (type, msg) => {
    clearTimeout(toastTimerRef.current);
    setToast({ type, msg });
    toastTimerRef.current = setTimeout(() => setToast(null), 3000);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const result = await docService.getAll({
        q: searchQuery || undefined,
        q_mode: qMode,     // derived from URL — always in sync
        page,
        size: PAGE_SIZE,
        type: typeFilter || undefined,
        status: statusFilter || undefined,
        site_id: siteFilter || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      setDocuments(result.items);
      setTotal(result.total);
      setTotalPages(result.pages ?? Math.ceil(result.total / PAGE_SIZE));
      setSelected(new Set());
    } catch (error) {
      // Never leave the previous page of results on screen after a failed
      // fetch: the filters would appear to have been applied when they had
      // not, which is worse than showing nothing. Clear and say so.
      console.error('Fetch docs failed', error);
      setDocuments([]);
      setTotal(0);
      setTotalPages(1);
      showToast('error', apiError(error, 'Could not load documents'));
    } finally {
      setLoading(false);
    }
  // qMode intentionally included — it comes from URL so changes correctly trigger re-fetch
  }, [searchQuery, qMode, page, typeFilter, statusFilter, siteFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const applySearchTerms = useCallback((terms, mode) => {
    const combined = terms.filter(t => t.trim()).join(',');
    setSearchQuery(combined);
    setQMode(mode);
    setPage(1);
  }, []);

  const debouncedApplyRef = useRef(null);
  if (!debouncedApplyRef.current) {
    debouncedApplyRef.current = _.debounce((terms, mode) => {
      applySearchTerms(terms, mode);
    }, 500);
  }

  const handleTermChange = (idx, value) => {
    const next = [...searchTerms];
    next[idx] = value;
    setSearchTerms(next);
    debouncedApplyRef.current(next, qMode);
  };

  const addTerm = () => setSearchTerms(prev => [...prev, '']);
  const removeTerm = (idx) => {
    const next = searchTerms.filter((_, i) => i !== idx);
    const resolved = next.length ? next : [''];
    setSearchTerms(resolved);
    applySearchTerms(resolved, qMode);
  };
  const toggleMode = () => {
    const next = qMode === 'or' ? 'and' : 'or';
    applySearchTerms(searchTerms, next);
  };

  // Delete handler (admin only)
  const handleDelete = async (id) => {
    setDeleting(true);
    try {
      await docService.delete(id);
      setDeleteConfirmId(null);
      await fetchDocuments();
      showToast('success', 'Document deleted successfully.');
    } catch (err) {
      showToast('error', 'Failed to delete document. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // Bulk download helpers
  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleSelectAll = () => {
    if (selected.size === documents.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(documents.map(d => d.id)));
    }
  };
  const handleBulkDownload = async () => {
    if (!selected.size) return;
    setBulkLoading(true);
    try {
      const blob = await docService.bulkDownload([...selected]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Issue 1: use search query as ZIP name when present
      const safeName = searchQuery
        ? searchQuery.replace(/[,\s]+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 60)
        : new Date().toISOString().slice(0, 10);
      a.download = `documents_${safeName}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Bulk download failed', err);
    } finally {
      setBulkLoading(false);
    }
  };

  const typedQuery = searchTerms.filter((t) => t.trim()).join(',');
  const searchPending = typedQuery !== searchQuery;
  const hasFilters = Boolean(typeFilter || statusFilter || siteFilter || searchQuery);
  const clearFilters = () => {
    setTypeFilter('');
    setStatusFilter('');
    setSiteFilter('');
    setSearchTerms(['']);
    applySearchTerms([''], qMode);
  };

  const selectClass =
    'text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="page-container h-full flex flex-col">
      {toast && (
        <div
          role="status"
          className={`fixed top-5 right-5 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
            toast.type === 'success' ? 'bg-slate-900 text-white' : 'bg-red-600 text-white'
          }`}
        >
          {toast.type === 'success' ? <HiCheckCircle className="w-4 h-4" /> : <HiExclamationCircle className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}

      <div className="flex items-end justify-between gap-4 mb-5">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Documents</h2>
          <p className="text-sm text-slate-400 mt-0.5">
            {total > 0 ? `${total.toLocaleString()} documents` : 'No documents match the current filters'}
            {searchQuery && (
              <span className="text-slate-500">
                {' '}· matching <span className="font-medium text-slate-600">{searchQuery}</span>
              </span>
            )}
            {searchPending && <span className="text-slate-400"> · searching…</span>}
          </p>
        </div>
        <button
          onClick={() => setShowLegend((v) => !v)}
          className="shrink-0 text-xs font-medium text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg px-2.5 py-1.5"
        >
          {showLegend ? 'Hide status guide' : 'What do statuses mean?'}
        </button>
      </div>

      {/* Collapsed by default: a permanent explainer block costs vertical space
          on every visit to answer a question asked once. */}
      {showLegend && (
        <div className="card mb-4 py-3">
          <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            {Object.entries(DOC_STATUS)
              .filter(([k]) => k !== 'deleted')
              .map(([key, { label, color }]) => (
                <div key={key} className="flex items-start gap-2">
                  <span
                    className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0"
                    style={{ backgroundColor: color }}
                    aria-hidden="true"
                  />
                  <div>
                    <dt className="font-medium text-slate-700">{label}</dt>
                    <dd className="text-xs text-slate-500">
                      {key === 'processing' && 'OCR and indexing in progress — not searchable yet.'}
                      {key === 'completed' && 'OCR and indexing finished — fully searchable.'}
                      {key === 'failed' && 'OCR or indexing errored — content unavailable.'}
                    </dd>
                  </div>
                </div>
              ))}
          </dl>
        </div>
      )}

      {/* One filter row above everything it scopes */}
      <div className="card mb-5 py-4">
        <div className="flex flex-wrap items-start gap-4">
          <div className="flex-1 min-w-[320px] space-y-2">
            {searchTerms.map((term, idx) => (
              <div key={idx} className="flex items-center gap-2">
                {idx === 0 ? (
                  searchTerms.length > 1 && (
                    <span className="w-11 shrink-0 text-center text-[11px] font-semibold text-slate-400 uppercase tracking-wide">
                      Where
                    </span>
                  )
                ) : (
                  <button
                    onClick={toggleMode}
                    title="Toggle AND / OR"
                    className="w-11 shrink-0 px-2 py-1 text-[11px] font-semibold uppercase rounded border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100"
                  >
                    {qMode}
                  </button>
                )}
                <div className="relative flex-1">
                  <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" aria-hidden="true" />
                  <input
                    type="text"
                    placeholder="Document number, vendor, or text inside the file…"
                    className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={term}
                    onChange={(e) => handleTermChange(idx, e.target.value)}
                  />
                </div>
                {searchTerms.length > 1 && (
                  <button
                    onClick={() => removeTerm(idx)}
                    className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"
                    title="Remove condition"
                  >
                    <HiX size={15} />
                  </button>
                )}
              </div>
            ))}
            <button
              onClick={addTerm}
              className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
            >
              <HiPlus size={13} /> Add condition
            </button>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            {(user?.is_superuser || userSites.length > 1) && (
              <select
                className={selectClass}
                value={siteFilter}
                onChange={(e) => { setSiteFilter(e.target.value); setPage(1); }}
                aria-label="Filter by site"
              >
                <option value="">All sites</option>
                {userSites.map((site) => (
                  <option key={site.site_id} value={site.site_id}>{site.site_name}</option>
                ))}
              </select>
            )}

            <select
              className={selectClass}
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
              aria-label="Filter by document type"
            >
              <option value="">All types</option>
              {allowedDocTypes(null, allTypeKeys).map((type) => (
                <option key={type} value={type}>{labelOf(type)}</option>
              ))}
            </select>

            <select
              className={selectClass}
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              aria-label="Filter by status"
            >
              <option value="">All statuses</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>

            {hasFilters && (
              <button
                onClick={clearFilters}
                className="text-xs font-medium text-slate-500 hover:text-slate-700 px-2 py-2"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="mb-4 flex items-center gap-3 px-4 py-2.5 bg-slate-900 text-white rounded-lg">
          <span className="text-sm font-medium">{selected.size} selected</span>
          <button
            onClick={handleBulkDownload}
            disabled={bulkLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-sm rounded-md disabled:opacity-50"
          >
            {bulkLoading
              ? <AiOutlineLoading3Quarters size={14} className="animate-spin" />
              : <MdDownload size={14} />}
            Download
          </button>
          <button
            onClick={() => setSelected(new Set())}
            className="ml-auto text-xs text-slate-300 hover:text-white"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* On refetch the previous rows stay put at reduced opacity — no skeleton
          flash, no layout jump, and the numbers never blank out mid-read. */}
      <div className={`card p-0 overflow-hidden flex-1 min-h-[16rem] transition-opacity ${loading ? 'opacity-60' : ''}`}>
        <div className="h-full overflow-auto">
          <table className="w-full text-left border-separate" style={{ borderSpacing: 0 }}>
            <thead>
              <tr>
                <th
                  className={`px-4 py-3 sticky left-0 top-0 z-40 bg-white ${HEADER_RULE}`}
                  style={{ width: STICKY_CHECKBOX_W, minWidth: STICKY_CHECKBOX_W }}
                >
                  <input
                    type="checkbox"
                    checked={documents.length > 0 && selected.size === documents.length}
                    onChange={toggleSelectAll}
                    className="rounded border-slate-300 accent-blue-600"
                    aria-label="Select all rows"
                  />
                </th>
                {[
                  { label: 'Document', field: 'filename' },
                  { label: 'Type', field: 'document_type' },
                  { label: 'Site', field: null },
                  { label: 'Status', field: 'status' },
                  { label: 'Created', field: 'created_at' },
                  { label: 'Last checked', field: 'last_checked_at' },
                ].map(({ label, field }) => (
                  <th
                    key={label}
                    className={`px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap ${
                      field && !searchQuery ? 'cursor-pointer select-none hover:text-slate-700' : ''
                    } ${HEADER_RULE} sticky top-0 bg-white ${label === 'Document' ? `z-40 ${STICKY_LEFT_EDGE}` : 'z-30'}`}
                    style={label === 'Document' ? { left: STICKY_CHECKBOX_W } : undefined}
                    onClick={() => field && !searchQuery && handleSort(field)}
                    title={field && searchQuery ? 'Sorting is disabled while searching' : undefined}
                  >
                    {label}
                    {field && !searchQuery && <SortIcon field={field} sortBy={sortBy} sortOrder={sortOrder} />}
                  </th>
                ))}
                <th
                  className={`px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500 text-right sticky right-0 top-0 z-40 bg-white ${HEADER_RULE} ${STICKY_RIGHT_EDGE}`}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {documents.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-16 text-center">
                    <p className="text-slate-500 text-sm">
                      {loading ? 'Loading documents…' : 'No documents match these filters.'}
                    </p>
                    {!loading && hasFilters && (
                      <button
                        onClick={clearFilters}
                        className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-700"
                      >
                        Clear filters
                      </button>
                    )}
                  </td>
                </tr>
              ) : documents.map((doc) => (
                <tr
                  key={doc.id}
                  className={rowClass(selected.has(doc.id))}
                >
                  <td
                    className={`px-4 py-3 align-top sticky left-0 z-20 ${ROW_RULE} ${stickyBg(selected.has(doc.id))}`}
                    style={{ width: STICKY_CHECKBOX_W, minWidth: STICKY_CHECKBOX_W }}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(doc.id)}
                      onChange={() => toggleSelect(doc.id)}
                      className="mt-1 rounded border-slate-300 accent-blue-600"
                      aria-label={`Select ${doc.filename}`}
                    />
                  </td>

                  <td
                    className={`px-4 py-3 sticky z-20 ${ROW_RULE} ${stickyBg(selected.has(doc.id))} ${STICKY_LEFT_EDGE}`}
                    style={{ left: STICKY_CHECKBOX_W, minWidth: 260, maxWidth: 360 }}
                  >
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 shrink-0 px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px] font-semibold tracking-wide">
                        {getFileExt(doc.filename)}
                      </span>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800 text-sm truncate" title={doc.filename}>
                          {doc.filename}
                        </p>
                        {doc.ocr_text && (
                          // Rendered as escaped React nodes, never innerHTML —
                          // OCR text is document content and must not be trusted.
                          <HighlightedText
                            text={doc.ocr_text}
                            className="block text-xs text-slate-400 mt-0.5 line-clamp-2"
                          />
                        )}
                      </div>
                    </div>
                  </td>

                  <td className="border-b border-slate-50 px-4 py-3 align-top">
                    {doc.document_type ? (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 whitespace-nowrap">
                        {labelOf(doc.document_type)}
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>

                  <td className="border-b border-slate-50 px-4 py-3 align-top text-sm text-slate-600 whitespace-nowrap">
                    {siteMap[doc.site_id]?.name ?? <span className="text-slate-300">—</span>}
                  </td>

                  <td className="border-b border-slate-50 px-4 py-3 align-top whitespace-nowrap">
                    {(() => {
                      const key = doc.status || (doc.score ? 'completed' : 'processing');
                      const info = DOC_STATUS[key] ?? { label: key, color: '#898781' };
                      return (
                        <span className="inline-flex items-center gap-1.5 text-sm text-slate-700">
                          <span
                            className="w-1.5 h-1.5 rounded-full shrink-0"
                            style={{ backgroundColor: info.color }}
                            aria-hidden="true"
                          />
                          {info.label}
                        </span>
                      );
                    })()}
                  </td>

                  <td className="border-b border-slate-50 px-4 py-3 align-top text-sm text-slate-600 whitespace-nowrap tabular-nums">
                    {formatDate(doc.created_at)}
                  </td>
                  <td className="border-b border-slate-50 px-4 py-3 align-top text-sm text-slate-400 whitespace-nowrap tabular-nums">
                    {formatDateTime(doc.last_checked_at)}
                  </td>

                  <td
                    className={`px-4 py-3 align-top sticky right-0 z-20 ${ROW_RULE} ${stickyBg(selected.has(doc.id))} ${STICKY_RIGHT_EDGE}`}
                  >
                    {deleteConfirmId === doc.id ? (
                      <div className="flex items-center justify-end gap-1.5">
                        <span className="text-xs text-slate-500">Delete?</span>
                        <button
                          onClick={() => handleDelete(doc.id)}
                          disabled={deleting}
                          className="px-2 py-1 text-xs font-medium bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                        >
                          {deleting ? '…' : 'Yes'}
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(null)}
                          className="px-2 py-1 text-xs text-slate-600 rounded hover:bg-slate-100"
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-0.5">
                        <Link
                          to={`/documents/${doc.id}`}
                          target="_blank"
                          title="Open document"
                          className="p-2 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                        >
                          <FaEye size={14} />
                        </Link>
                        {doc.download_url && (
                          <a
                            href={doc.download_url}
                            download={doc.filename || `document-${doc.id}.pdf`}
                            title="Download"
                            className="p-2 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                          >
                            <MdDownload size={14} />
                          </a>
                        )}
                        {user?.is_superuser && (
                          <button
                            onClick={() => setDeleteConfirmId(doc.id)}
                            title="Delete"
                            className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
                          >
                            <HiTrash size={14} />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 flex justify-between items-center text-sm text-slate-500">
        <span className="tabular-nums">
          {total === 0
            ? 'No results'
            : `Showing ${((page - 1) * PAGE_SIZE + 1).toLocaleString()}–${Math.min(page * PAGE_SIZE, total).toLocaleString()} of ${total.toLocaleString()}`}
        </span>
        <Pagination page={page} totalPages={totalPages} onChange={(p) => setPage(p)} />
      </div>
    </div>
  );
};

export default DocumentsPage;
